import { Box, Button, Checkbox, Flex, Text, Textarea } from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';

import {
  ResolvedLoanFragmentGQLType,
  InvoiceItemInputGQLType,
  InvoiceTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import {
  CreateInvoiceHeaderWithTimelineStepper,
  CreateInvoiceStepIndex,
} from './CreateInvoiceHeaderWithTimelineStepper';
import {
  CreateInvoiceLoanSelectorTable,
  CreateInvoiceLoanSelectorTableSkeleton,
} from './CreateInvoiceLoanSelectorTable';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';
import { SearchSelect } from '~/packages/common-frontend/components/SearchSelect';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { useGetCreditFacilitiesFullLoansQuery } from '~/packages/loans/requests/loans.gql.generated';

const invoiceTypeOptions: { value: InvoiceTypeGQLType; label: string }[] = [
  { value: 'loan', label: 'Loan' },
  { value: 'creditFacility', label: 'Facility' },
];

export const CreateInvoiceDetails = ({
  setCurrentStepIndex,
}: {
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
}) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const navigate = useNavigate();
  const {
    user: { organizationId },
  } = useUserAuthOrThrow();
  const { facilities } = useCreditFacilitiesOrThrow();

  const {
    invoice,
    invoiceComponentsWithInvoiceItems,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
    updateInvoice,
    resetInvoice,
    resetInvoiceComponents,
    selectedEntities: {
      loan: { loan: selectedLoan, updateLoan },
      creditFacility: { creditFacility, updateCreditFacility },
    },
  } = useCreateInvoiceContext();

  const {
    data: loansData,
    loading: isLoansLoading,
  } = useGetCreditFacilitiesFullLoansQuery({
    variables: { organizationId },
  });

  const creditFacilityOptions = useMemo(() => {
    return facilities.map((facility) => ({
      value: facility.id,
      label: facility.name,
    }));
  }, [facilities]);

  const loansByFacility = useMemo(() => {
    if (isLoansLoading || !loansData?.creditFacilities) {
      return undefined;
    }

    return loansData.creditFacilities.reduce<
      Record<string, ResolvedLoanFragmentGQLType[]>
    >((acc, facility) => {
      // eslint-disable-next-line no-param-reassign
      acc[facility.id] = facility.loans;
      return acc;
    }, {});
  }, [loansData, isLoansLoading]);

  const loansForSelectedCreditFacility:
    | ResolvedLoanFragmentGQLType[]
    | undefined = loansByFacility?.[invoice.creditFacilityId];

  const onCancel = useCallback(() => {
    resetInvoice();
    navigate('/invoices');
  }, [resetInvoice, navigate]);

  const canClickContinue = useMemo(() => {
    if (!invoice.creditFacilityId && !invoice.invoiceType && !invoice.dueDate) {
      return false;
    }

    if (invoice.invoiceType === 'loan' && !invoice.finleyLoanId) {
      return false;
    }

    return true;
  }, [invoice]);

  const minAndMaxDueDate = useMemo(() => {
    const today = new Date();
    const fiveYearsLater = new Date().setFullYear(today.getFullYear() + 5);

    return {
      minDate: selectedLoan
        ? commonDate.fullDateStrToDate(selectedLoan.dates.originationDate)
        : today,
      maxDate: new Date(fiveYearsLater),
    };
  }, [selectedLoan]);

  return (
    <Flex direction="column" h="100vh">
      {/* Header */}
      <Box mb="40px">
        <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={0} />
      </Box>

      {/* Guided Form Step 1 */}
      <Flex direction="column" flexGrow={1}>
        <Box mb="24px">
          <Text fontWeight={600} mb="16px">
            Facility
          </Text>
          <SearchSelect
            options={creditFacilityOptions}
            value={creditFacilityOptions.find(
              (option) => option.value === invoice.creditFacilityId,
            )}
            onChange={(nextCreditFacilityId) => {
              updateInvoice({
                field: 'creditFacilityId',
                value: nextCreditFacilityId,
              });

              const newCreditFacility = facilities.find(
                (facility) => facility.id === nextCreditFacilityId,
              );

              finleyAssert(
                newCreditFacility,
                `Credit facility must exist for [creditFacilityId: ${nextCreditFacilityId}]`,
              );

              updateCreditFacility(newCreditFacility);

              // update payment source & destination org ids based on selected cf
              // currently we only allow payment source to be borrower
              // and payment destination to be admin agent cap provider
              updateInvoice({
                field: 'paymentSourceOrganizationId',
                value: newCreditFacility.borrowerId,
              });
              // @dani @sachinr4 todo - replace with admin agent field resolver
              // once the method of denoting the admin agent cap provider is decided
              updateInvoice({
                field: 'paymentDestinationOrganizationId',
                value:
                  newCreditFacility.capitalProviderRepresentativeOrganization
                    ?.id,
              });

              // reset invoice type to default
              updateInvoice({
                field: 'invoiceType',
                value: 'loan',
              }); // @dani multi-invoice todo: deprecate. this will be moved down to the invoice component level

              // reset finleyLoanId
              updateInvoice({
                field: 'finleyLoanId',
                value: undefined,
              }); // @dani multi-invoice todo: deprecate. this will be moved down to the invoice component level

              // reset invoice items
              resetInvoiceComponents();
            }}
            isSelectedColor={colors.gray[50]}
            isFocusedColor={colors.gray[50]}
          />
          {invoice.creditFacilityId && creditFacility && (
            <Text mt="8px" color={colors.gray[500]} fontSize="14px">
              External Facility ID: {creditFacility.externalFacilityId ?? '-'}
            </Text>
          )}
        </Box>
        <Box mb="24px">
          <Text fontWeight={600} mb="16px">
            Type
          </Text>
          <SearchSelect
            options={invoiceTypeOptions}
            value={invoiceTypeOptions.find(
              (option) => option.value === invoice.invoiceType,
            )}
            onChange={(nextInvoiceType) => {
              updateInvoice({
                field: 'invoiceType',
                value: nextInvoiceType,
              });

              // reset finleyLoanId and the selectedLoan entity if any other invoice type is chosen
              if (nextInvoiceType !== 'loan') {
                updateInvoice({
                  field: 'finleyLoanId',
                  value: undefined,
                }); // @dani multi-invoice todo: deprecate. this will be moved down to the invoice component level

                updateLoan(null);
              }
            }}
            isSelectedColor={colors.gray[50]}
            isFocusedColor={colors.gray[50]}
            isDisabled // @dani todo: we will current focus on supporting loan-level invoices. remove isDisabled flag once we implement support for facility-level invoices (eg. line items, disbursements)
          />
          {invoice.invoiceType === 'loan' &&
            creditFacility &&
            !loansForSelectedCreditFacility &&
            !!isLoansLoading && <CreateInvoiceLoanSelectorTableSkeleton />}
          {invoice.invoiceType === 'loan' &&
            creditFacility &&
            loansForSelectedCreditFacility && (
              <CreateInvoiceLoanSelectorTable
                loans={loansForSelectedCreditFacility}
              />
            )}
        </Box>
        {loansForSelectedCreditFacility && (
          <Box mb="24px">
            <Text fontWeight={600} mb="16px">
              Due Date
            </Text>
            <SimpleDatePicker
              selectedDate={commonDate.fullDateStrToDate(invoice.dueDate)}
              setSelectedDate={(date: Date | null) => {
                // date must exist. this datepicker is not clearable
                if (date) {
                  const newDueDate = commonDate.dateToFullDateString(
                    date,
                    clientTimeZone,
                  );
                  updateInvoice({ field: 'dueDate', value: newDueDate });

                  // @dani multi-invoice todo: deprecate. this will be moved down to the invoice component level
                  updateInvoice({ field: 'accrualEndDate', value: newDueDate }); // we want the invoice to default to represent the accrual period up to the invoice due date, since the loan can continue to accrue interest after the maturity date

                  invoiceComponentsWithInvoiceItems.forEach(
                    (invoiceComponent, idx) => {
                      const invoiceItemsForComponent =
                        invoiceComponent.invoiceItems;

                      // update new invoice items to remove PIK items capitalized on loans that no longer qualify for PIK
                      const newInvoiceItems: InvoiceItemInputGQLType[] = invoiceItemsForComponent.filter(
                        (invoiceItem) => {
                          const capitalizedLoan = loansForSelectedCreditFacility?.find(
                            (loan) => loan.id === invoiceItem.capitalizedLoanId,
                          );

                          // keep invoice items where it's Cash type OR
                          // for PIK types, the maturity date of the selected PIK capitalized loan for the invoice item is still greater than the new invoice due date
                          return (
                            !invoiceItem.capitalizedLoanId ||
                            (capitalizedLoan?.dates.maturityDate &&
                              capitalizedLoan.dates.maturityDate > newDueDate)
                          );
                        },
                      );
                      updateInvoiceItemsAndTotalsForInvoiceComponent({
                        invoiceComponentIndex: idx,
                        newInvoiceItemsForInvoiceComponent: newInvoiceItems,
                      });
                    },
                  );
                }
              }}
              width="100%"
              dateFormat="MMMM d, yyyy"
              minDate={minAndMaxDueDate.minDate}
              maxDate={minAndMaxDueDate.maxDate}
            />
            <Checkbox
              mt="16px"
              onChange={(e) =>
                updateInvoice({
                  field: 'autoSettle',
                  value: Boolean(e.target.checked),
                })
              }
              isChecked={invoice.autoSettle}
            >
              <Text color={colors.gray[500]} fontSize="14px">
                Automatically mark as &apos;Paid&apos; on Due Date.
              </Text>
            </Checkbox>
          </Box>
        )}
        {/* @dani currently we do not want to auto-send invoice */}
        {/* <Box mb="24px">
          <Flex alignItems="center" mb="16px" justifyContent="space-between">
            <Text fontWeight={600}>Invoice Date</Text>
            <Text color={colors.gray[500]}>Optional</Text>
          </Flex>
          <SimpleDatePicker
            showIcon
            selectedDate={
              invoice.targetInvoiceDate
                ? commonDate.fullDateStrToDate(invoice.targetInvoiceDate)
                : undefined
            }
            setSelectedDate={(date: Date | null) => {
              // date may not exist. this date picker is clearable
              if (date) {
                const newTargetInvoiceDate = commonDate.dateToFullDateString(
                  date,
                  clientTimeZone,
                );
                updateInvoice({
                  field: 'targetInvoiceDate',
                  value: newTargetInvoiceDate,
                });
              } else {
                updateInvoice({
                  field: 'targetInvoiceDate',
                  value: null,
                });
              }
            }}
            width="100%"
            dateFormat="MMMM d, yyyy"
            isClearable
          />
          <Text mt="8px" color={colors.gray[500]} fontSize="14px">
            Select the date to automatically send this invoice.
          </Text>
        </Box> */}
        <Box mb="24px">
          <Flex alignItems="center" justifyContent="space-between" mb="16px">
            <Text fontWeight={600}>Memo</Text>
            <Text color={colors.gray[500]}>Optional</Text>
          </Flex>
          <Textarea
            value={invoice.memo ?? ''}
            onChange={(e) => {
              updateInvoice({ field: 'memo', value: e.target.value });
            }}
            placeholder="Leave a memo"
          />
        </Box>
      </Flex>

      {/* Form Navigation */}
      <Flex justifyContent="space-between" py="40px">
        <Button variant="secondary" colorScheme="v2-aurora" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          colorScheme="v2-aurora"
          isDisabled={!canClickContinue}
          onClick={() => setCurrentStepIndex(1)}
        >
          Continue
        </Button>
      </Flex>
    </Flex>
  );
};
